import { useState } from 'react';
import { Play, Loader2, AlertTriangle, FileCheck2 } from 'lucide-react';
import { getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FilterToolbar, { FilterForm } from './FilterToolbar';

export default function SslValidator() {
  const [sslFiles, setSslFiles] = useState([]);
  const [oracleFile, setOracleFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [issueType, setIssueType] = useState('');

  const runValidation = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    try {
      if (!sslFiles.length) throw new Error('Select one or more SSL files.');
      const fd = new FormData();
      for (const f of sslFiles) fd.append('sslFiles', f);
      if (oracleFile) fd.append('oracleMaster', oracleFile);
      const base = getApiBase();
      const res = await fetch(`${base}/api/ssl/validate`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Validation failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Validation failed');
    } finally {
      setRunning(false);
    }
  };

  const issues = result?.issues || [];
  const needle = filter.trim().toLowerCase();
  const types = result?.issuesByType ? Object.keys(result.issuesByType) : [];
  const visible = issues.filter((r) => {
    if (issueType && r.type !== issueType) return false;
    if (!needle) return true;
    return [r.person, r.wageType, r.effectiveDate, r.detail, r.type, r.files]
      .map((v) => String(v ?? '').toLowerCase())
      .some((v) => v.includes(needle));
  });

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Pre-payroll validation</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">SSL intake validator</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
          Upload one or more SSL (wage-type load) files. Flags duplicate person + effective date +
          wage type keys, zero/missing amounts, missing dates, and (optionally) people not in the
          Oracle master. Only one record per person/date/wage type will load.
        </p>
      </header>

      <ReconSection
        title="Files & run"
        description="Multiple SSL files supported. Oracle master is optional (for person validation)."
        headerCenter={
          <button type="button" onClick={runValidation} disabled={running} className="btn-primary">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Validate
          </button>
        }
      >
        <div className="grid gap-3">
          <label className="file-input-wrap cursor-pointer text-sm">
            <span className="font-medium text-slate-200">SSL files</span>
            <span className="text-xs text-slate-500">.xlsx / .csv — select multiple</span>
            <input
              type="file"
              multiple
              accept=".xlsx,.xls,.csv"
              className="text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-100 hover:file:bg-slate-700"
              onChange={(e) => setSslFiles(Array.from(e.target.files || []))}
            />
            {sslFiles.length > 0 && (
              <span className="truncate text-xs font-mono text-emerald-400">
                {sslFiles.map((f) => f.name).join(', ')}
              </span>
            )}
          </label>
          <label className="file-input-wrap cursor-pointer text-sm">
            <span className="font-medium text-slate-200">Oracle master (optional)</span>
            <span className="text-xs text-slate-500">.xlsx — validates person numbers</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-100 hover:file:bg-slate-700"
              onChange={(e) => setOracleFile(e.target.files?.[0] || null)}
            />
            {oracleFile && <span className="truncate text-xs font-mono text-emerald-400">{oracleFile.name}</span>}
          </label>
        </div>
        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>

      {result && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-neutral inline-flex items-center gap-1">
              <FileCheck2 className="h-3.5 w-3.5" />
              {result.fileCount} file(s) · {result.totalRows?.toLocaleString()} rows ·{' '}
              {result.uniquePersons?.toLocaleString()} persons
            </span>
            <span className={result.issueCount ? 'badge-warning inline-flex items-center gap-1' : 'badge-success inline-flex items-center gap-1'}>
              <AlertTriangle className="h-3.5 w-3.5" />
              {result.issueCount?.toLocaleString()} issue(s)
            </span>
          </div>

          {types.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {types.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setIssueType(issueType === t ? '' : t)}
                  className={issueType === t ? 'badge-warning' : 'badge-neutral'}
                >
                  {t}: {result.issuesByType[t].toLocaleString()}
                </button>
              ))}
            </div>
          )}

          <div className="card-panel overflow-hidden">
            <div className="section-header flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-100">Issues</h3>
              <FilterForm onApply={() => setFilter(filterDraft)}>
                <FilterToolbar
                  onClear={() => {
                    setFilterDraft('');
                    setFilter('');
                  }}
                  hasActiveFilters={!!filter.trim()}
                >
                  <input
                    type="search"
                    placeholder="Person, wage type, type…"
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="filter-input min-w-[12rem] text-sm"
                  />
                </FilterToolbar>
              </FilterForm>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Person</th>
                    <th>Wage type</th>
                    <th>Effective date</th>
                    <th>File(s)</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-500">
                        {result.issueCount ? 'No issues match the filter.' : 'No issues found.'}
                      </td>
                    </tr>
                  ) : (
                    visible.slice(0, 500).map((r, i) => (
                      <tr key={`${r.type}-${r.person}-${i}`}>
                        <td>{r.type}</td>
                        <td className="font-mono">{r.person}</td>
                        <td className="font-mono">{r.wageType || '—'}</td>
                        <td>{r.effectiveDate || '—'}</td>
                        <td className="max-w-[10rem] truncate" title={r.files}>{r.files || '—'}</td>
                        <td className="max-w-md truncate text-slate-400" title={r.detail}>{r.detail}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
